import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useChainProvider } from "../../lib/rpc/useChainProvider";
import { assessRisk, type RiskTier } from "../../lib/forensics/risk";
import type { GraduationEntry } from "../../lib/schemas";

const PINNED_REFRESH_MS = 10_000;
const chainLabel = (chain: GraduationEntry["chain"]) => (chain === "avalanche" ? "AVAX" : "RHC");
const riskColor: Record<RiskTier, string> = {
  UNAVAILABLE: "var(--flurry-mid)",
  CRITICAL: "var(--flurry-red)",
  HIGH: "var(--flurry-amber)",
  MODERATE: "var(--flurry-cyan)",
  LOW: "var(--flurry-green)",
};

export function Graduation() {
  const provider = useChainProvider();
  const [query, setQuery] = useState("");
  const [pinned, setPinned] = useState<GraduationEntry[]>([]);
  const [queueError, setQueueError] = useState<string | null>(null);

  const { data = [] } = useQuery({
    queryKey: ["listings", provider.id],
    queryFn: () => provider.getGraduationCandidates(),
    refetchInterval: 5000,
  });

  const pinnedRef = useRef(pinned);
  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);

  useEffect(() => {
    setPinned([]);
    setQuery("");
    setQueueError(null);
  }, [provider]);

  // User-pinned token addresses follow the same live listing refresh as feed tokens.
  useEffect(() => {
    const resolve = provider.resolveQueuedMint;
    if (!resolve) return;
    const t = setInterval(() => {
      for (const row of pinnedRef.current) {
        void resolve(row.mint)
          .then((fresh) =>
            setPinned((rows) => rows.map((r) => (r.mint === fresh.mint ? fresh : r))),
          )
          .catch(() => void 0);
      }
    }, PINNED_REFRESH_MS);
    return () => clearInterval(t);
  }, [provider]);

  const queue = () => {
    const q = query.trim();
    if (!q) return;
    setQueueError(null);
    if (provider.resolveQueuedMint) {
      provider
        .resolveQueuedMint(q)
        .then((entry) => setPinned((p) => [entry, ...p]))
        .catch((e: unknown) =>
          setQueueError(e instanceof Error ? e.message : "failed to resolve token address"),
        );
    } else {
      setQueueError("address lookup is unavailable from this hosted evidence source");
    }
    setQuery("");
  };

  const rows = [...pinned, ...data].sort((a, b) => Number(b.pinned) - Number(a.pinned));

  return (
    <div>
      <div className="mb-3 flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && queue()}
          placeholder="queue a token address to watch..."
          className="flex-1 px-2 py-1 text-sm outline-none"
          style={{
            background: "var(--flurry-panel)",
            border: "1px solid var(--flurry-dim)",
            color: "var(--flurry-green)",
          }}
        />
        <button
          onClick={queue}
          className="px-3 py-1 text-xs"
          style={{
            color: "var(--flurry-bg)",
            background: "var(--flurry-green)",
            border: "none",
            cursor: "pointer",
          }}
        >
          + WATCH
        </button>
      </div>
      {queueError && (
        <div className="mb-2 text-xs" style={{ color: "var(--flurry-red)" }}>
          {queueError}
        </div>
      )}
      <div className="mb-2 text-xs" style={{ color: "var(--flurry-mid)" }}>
        direct-pool listings // LISTED means verified venue evidence, not a bonding-curve graduation
      </div>
      <div style={{ border: "1px solid var(--flurry-dim)" }}>
        <div
          className="listing-grid grid px-2 py-1 text-xs"
          style={{
            gridTemplateColumns: "110px 100px 1fr 80px 80px 90px 100px 80px",
            color: "var(--flurry-mid)",
            borderBottom: "1px solid var(--flurry-dim)",
          }}
        >
          <span>TOKEN</span>
          <span>PLATFORM</span>
          <span>LIFECYCLE</span>
          <span>MCAP</span>
          <span>VOL/1H</span>
          <span>STATUS</span>
          <span>RISK</span>
          <span>COVERAGE</span>
        </div>
        {rows.map((g) => {
          const directPool = g.lifecycle === "direct-pool";
          const color =
            g.listingStatus === "verified" ? "var(--flurry-green)" : "var(--flurry-mid)";
          const risk = assessRisk(g.riskEvidence);
          return (
            <div
              key={g.mint}
              className="listing-grid grid px-2 py-1 text-sm"
              style={{
                gridTemplateColumns: "110px 100px 1fr 80px 80px 90px 100px 80px",
                borderBottom: "1px solid var(--flurry-dim)",
              }}
            >
              <span
                style={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {g.pinned && <span style={{ color: "var(--flurry-amber)" }}>★ </span>}
                <span style={{ color: "var(--flurry-mid)" }}>{chainLabel(g.chain)}·</span>$
                {g.ticker}
              </span>
              <span
                style={{
                  color: "var(--flurry-cyan)",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {g.platformLabel}
              </span>
              <span style={{ color }}>{directPool ? "— DIRECT POOL" : "— CANDIDATE"}</span>
              <span>
                {g.marketDataStatus === "verified"
                  ? `$${Math.round(g.mcapUsd / 1000)}k`
                  : "unavailable"}
              </span>
              <span style={{ color: g.volHoldersVerified ? undefined : "var(--flurry-mid)" }}>
                {g.volHoldersVerified ? `$${Math.round(g.vol1hUsd / 1000)}k` : "unverified"}
              </span>
              <span style={{ color }}>
                {directPool
                  ? g.listingStatus === "verified"
                    ? "LISTED"
                    : "UNVERIFIED"
                  : "CANDIDATE"}
              </span>
              <span
                style={{ color: riskColor[risk.tier] }}
                aria-label={`Risk ${risk.tier}, score ${risk.minScore} to ${risk.maxScore}`}
              >
                {risk.tier === "UNAVAILABLE" ? "RANGE" : risk.tier} {risk.minScore}–{risk.maxScore}
              </span>
              <span
                style={{ color: risk.coveragePct ? "var(--flurry-cyan)" : "var(--flurry-mid)" }}
                aria-label={`${risk.coveragePct}% risk evidence coverage, ${risk.confidence} confidence`}
              >
                {risk.coveragePct}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
